// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// 대시보드 위젯의 "숫자"를 누르면 그 숫자를 만든 실제 건 목록을 보여 주는 공용 모달.
//
// 남양주 위젯(12단계 흐름 A1 · 종결 보드 C · 모니터링 B8)이 모두 같은 계약을 쓴다:
//   요청  GET <endpoint>?<params>&page=&per_page=
//   응답  { items: [{id,title,subtitle,at,badge{label,tone},href}], pagination: {...},
//           stage|metric|service: { label, link: { href, label } } }
// 서버가 카드 숫자와 같은 스코프에서 목록을 만들기 때문에 "카드 숫자 ≠ 목록"이 없다.
//
// 카드마다 레코드 종류(케이스/방문/기관/의뢰…)가 달라도 서버가 위 공통 행 모양으로
// 눕혀 주므로 이 모달은 표 하나만 그린다.

const PER_PAGE = 25;

function fmtDate(iso, withTime) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("ko-KR", { year: "2-digit", month: "short", day: "numeric" });
    return withTime ? `${date} ${d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}` : date;
  } catch { return iso; }
}

export function MetricDrilldownModal({
  endpoint,           // 예: "/api/v1/dashboard/region/closure_board/items"
  params,             // 예: { metric: "month_closed" } — page/per_page 는 모달이 붙인다
  regionCode,         // 권역 코드(?code=) — system_admin 이 타 권역을 볼 때 필요
  title,              // 없으면 응답 meta.label 을 쓴다
  titleSuffix,        // 제목 옆 회색 보조 텍스트(급여코드 등)
  countLabel = "전체",// "전체 252건" / "제공 기관 3건"
  columns = ["대상", "내용", "상태", "일자"],
  withTime = false,   // 일자 칸에 시각까지 표시(방문 등)
  emptyText = "해당하는 건이 없습니다.",
  onClose,
}: any) {
  useEscapeKey(onClose);
  const [rows, setRows] = React.useState(null);
  const [meta, setMeta] = React.useState(null);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  const [page, setPage] = React.useState(1);
  const [err, setErr] = React.useState(null);

  // 파라미터가 바뀌면(다른 카드를 눌렀다면) 1페이지부터.
  const paramKey = JSON.stringify(params || {});
  React.useEffect(() => { setPage(1); }, [paramKey, endpoint]);

  React.useEffect(() => {
    let alive = true;
    setErr(null);
    setRows(null);
    const qs = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE) });
    Object.entries(params || {}).forEach(([k, v]) => { if (v != null && v !== "") qs.set(k, String(v)); });
    if (regionCode) qs.set("code", regionCode);
    fetch(`${endpoint}?${qs.toString()}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(async r => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
        return j;
      })
      .then(data => {
        if (!alive) return;
        setRows(data.items || []);
        setMeta(data.metric || data.stage || data.service || null);
        setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
      })
      .catch(e => { if (alive) setErr(apiErrorMessage(e)); });
    return () => { alive = false; };
  }, [endpoint, paramKey, regionCode, page]);

  const link = meta?.link;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="metric-drilldown-title"
      style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:80, display:'grid', placeItems:'center', padding:24}}
      onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} style={{background:'var(--surface)', borderRadius:14, width:'min(720px, 100%)', maxHeight:'min(80vh, 720px)', display:'flex', flexDirection:'column', boxShadow:'0 18px 60px rgba(0,0,0,0.25)', overflow:'hidden'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid var(--border-soft)'}}>
          <div>
            <div id="metric-drilldown-title" className="h3" style={{margin:0}}>
              {title || meta?.label || "목록"}
              {titleSuffix && (
                <span className="muted mono" style={{fontSize:11, fontWeight:600, marginLeft:8}}>{titleSuffix}</span>
              )}
            </div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {rows === null
                ? "불러오는 중…"
                : pagination.total_pages > 1
                  ? `${countLabel} ${pagination.total.toLocaleString()}건 · 페이지 ${pagination.page}/${pagination.total_pages}`
                  : `${countLabel} ${pagination.total.toLocaleString()}건`}
            </div>
          </div>
          <button type="button" aria-label="닫기" onClick={onClose}
            style={{background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer', padding:4}}>
            {Ic.close}
          </button>
        </div>

        <div style={{flex:1, overflow:'auto'}}>
          {err && <div style={{padding:22, color:'var(--rose-500)', fontSize:13}}>목록을 불러오지 못했습니다: {err}</div>}
          {!err && rows && rows.length === 0 && (
            <div className="muted" style={{padding:'32px 22px', textAlign:'center', fontSize:12}}>{emptyText}</div>
          )}
          {!err && rows && rows.length > 0 && (
            <table className="tbl" style={{width:'100%'}}>
              <thead>
                <tr>
                  <th style={{paddingLeft:22}}>{columns[0]}</th>
                  <th>{columns[1]}</th>
                  <th>{columns[2]}</th>
                  <th style={{paddingRight:22}}>{columns[3]}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}
                    onClick={r.href ? () => { window.location.href = r.href; } : undefined}
                    style={{cursor: r.href ? 'pointer' : 'default'}}
                    title={r.href ? `${r.title} 상세 보기` : r.title}>
                    <td style={{paddingLeft:22}}><b>{r.title || "-"}</b></td>
                    <td><span className="muted" style={{fontSize:12}}>{r.subtitle || "-"}</span></td>
                    <td>
                      {r.badge
                        ? <span className={"badge badge-" + (r.badge.tone || "gray")}>{r.badge.label}</span>
                        : <span className="muted">-</span>}
                    </td>
                    <td className="mono" style={{fontSize:12, paddingRight:22}}>{fmtDate(r.at, withTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10}}>
          {link
            ? <a href={link.href} className="btn btn-soft btn-md" style={{textDecoration:'none'}}
                 title="이 목록을 전체 화면에서 봅니다">{link.label}에서 보기 →</a>
            : <span />}
          {pagination.total_pages > 1 && (
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page <= 1}
                onClick={()=>setPage(p => Math.max(1, p - 1))}>이전</button>
              <span className="muted" style={{fontSize:12}}>{pagination.page} / {pagination.total_pages}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page >= pagination.total_pages}
                onClick={()=>setPage(p => Math.min(pagination.total_pages, p + 1))}>다음</button>
            </div>
          )}
          <button type="button" className="btn btn-soft btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
